// frontend/src/components/users/ManageCustomerAccessDrawer.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  Switch,
  Typography,
  message,
  Tooltip,
} from 'antd';
import {
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { CustomerDTO } from '../../services/customers';
import {
  getTeam,
  createUser,
  updateUser,
  checkEmailUnique,
  type UserDTO as User,
} from '../../services/users';

const { Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  customer: CustomerDTO | null; // deve contenere almeno { id, businessName, userId?, email? }
  onSaved?: () => void;
};

type FormVals = {
  email: string;
  firstName?: string;
  lastName?: string;
  active: boolean;
  // update
  resetPasswordOnUpdate?: boolean;
  newPassword?: string;
};

const randomPassword = (len = 14) => {
  const s = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
  return Array.from({ length: len }, () => s[Math.floor(Math.random() * s.length)]).join('');
};

const ManageCustomerAccessDrawer: React.FC<Props> = ({
  open,
  onClose,
  customer,
  onSaved,
}) => {
  const [form] = Form.useForm<FormVals>();
  const [loading, setLoading] = useState(false);
  const [existingUser, setExistingUser] = useState<User | null>(null);

  // password mostrata quando generata ORA (create o reset)
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(true);

  const title = useMemo(
    () => `Gestisci accessi – ${customer?.businessName ?? '—'}`,
    [customer?.businessName]
  );
  const userIdFromRow = useMemo(
    () => (customer && (customer as any).userId ? Number((customer as any).userId) : null),
    [customer]
  );
  const isEditing = !!existingUser?.id;

  // Bootstrap: se la riga ha userId, prendo l’utente vero da /p/users/team
  useEffect(() => {
    const load = async () => {
      setExistingUser(null);
      setTempPassword(null);
      setShowPassword(true);
      form.resetFields();

      if (!open || !customer?.id) return;

      try {
        setLoading(true);

        if (userIdFromRow) {
          const team = await getTeam();
          const found = team.find((u) => u.id === userIdFromRow) || null;
          setExistingUser(found || null);

          if (found) {
            // Prefill con i DATI REALI
            form.setFieldsValue({
              email: found.email,
              firstName: found.firstName || '',
              lastName: found.lastName || '',
              active: (found as any).isActive ?? true,
              resetPasswordOnUpdate: false,
              newPassword: undefined,
            });
            return;
          }
        }

        // Nessun accesso collegato: CREAZIONE -> precompila con email del customer (se presente)
        form.setFieldsValue({
          email: (customer as any)?.email || '',
          firstName: '',
          lastName: '',
          active: true,
          resetPasswordOnUpdate: false,
          newPassword: undefined,
        });

        // genera SUBITO una password (editabile) in creazione
        const generated = randomPassword();
        setTempPassword(generated);
      } catch (err: any) {
        message.error(err?.message || 'Errore durante il caricamento degli accessi');
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      form.resetFields();
      setExistingUser(null);
      setTempPassword(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.id, userIdFromRow]);

  const copyPwd = async () => {
    if (!tempPassword) return;
    await navigator.clipboard.writeText(tempPassword);
    message.success('Password copiata');
  };

  const regenPwd = () => {
    const pwd = randomPassword();
    setTempPassword(pwd);
    // se siamo in update con campo visibile, tieni sync il form
    if (form.getFieldValue('resetPasswordOnUpdate')) {
      form.setFieldsValue({ newPassword: pwd });
    }
  };

  const handleSubmit = async (vals: FormVals) => {
    if (!customer?.id) return;

    const {
      email,
      firstName = '',
      lastName = '',
      active,
      resetPasswordOnUpdate,
      newPassword,
    } = vals;

    // 👉 Default obbligatori per il backend se vuoti
    const safeFirst = firstName.trim() || 'Cliente';
    const safeLast  = lastName.trim()  || 'Portale';

    // ruolo fisso CUSTOMER
    const base = {
      email: email.trim(),
      firstName: safeFirst,
      lastName: safeLast,
      role: 'CUSTOMER' as const,
      active: !!active, // mappato dal backend su isActive
    };

    try {
      setLoading(true);

      if (isEditing && existingUser?.id) {
        // UPDATE esistente
        const payload: any = { ...base };

        if (resetPasswordOnUpdate) {
          const chosen =
            (newPassword && newPassword.trim()) ||
            tempPassword ||
            randomPassword();
          payload.password = chosen;
          setTempPassword(chosen);
        }

        await updateUser(existingUser.id, payload);

        message.success(
          resetPasswordOnUpdate
            ? 'Accesso aggiornato. Nuova password impostata (vedi sotto).'
            : 'Accesso aggiornato.'
        );
      } else {
        // CREATE: verifica univocità email prima di procedere
        try {
          const chk = await checkEmailUnique(base.email);
          if (!chk.available) {
            message.error('Email già in uso. Scegline un’altra.');
            return;
          }
        } catch {
          // non blocco in caso di errore di rete sul check, ma avviso
          message.warning('Impossibile verificare l’unicità email ora. Procedo comunque.');
        }

        // crea utente e AGGANCIA al customer in un colpo solo (customerId nel payload)
        const chosen = (tempPassword && tempPassword.trim()) || randomPassword();
        setTempPassword(chosen);

        await createUser({
          ...base,
          password: chosen,
          customerId: customer.id!, // ONE-SHOT LINK lato backend
        });

        message.success('Accesso creato e collegato al cliente.');
      }

      onSaved?.();

      // se non c’è password da copiare, posso chiudere
      if (!tempPassword) onClose();
    } catch (err: any) {
      message.error(err?.message || 'Errore durante il salvataggio');
      // non chiudo per non perdere l’eventuale password appena generata
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Chiudi</Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading}>
            Salva
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" form={form} onFinish={handleSubmit} disabled={loading}>
        {existingUser && (
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary">
              Utente collegato: ID {existingUser.id} – Ruolo: {existingUser.role}
            </Text>
          </div>
        )}

        <Form.Item
          label="Email (login)"
          name="email"
          rules={[
            { required: true, message: "Inserisci l'email" },
            { type: 'email', message: 'Email non valida' },
            // validazione async (solo in creazione)
            () => ({
              async validator(_, value) {
                if (!value || isEditing) return Promise.resolve();
                try {
                  const r = await checkEmailUnique(String(value).trim());
                  return r.available ? Promise.resolve() : Promise.reject('Email già in uso');
                } catch {
                  // se il check fallisce non blocco la compilazione
                  return Promise.resolve();
                }
              },
            }),
          ]}
        >
          <Input placeholder="email@dominio.tld" />
        </Form.Item>

        <Form.Item label="Nome" name="firstName">
          <Input placeholder="(opzionale)" />
        </Form.Item>

        <Form.Item label="Cognome" name="lastName">
          <Input placeholder="(opzionale)" />
        </Form.Item>

        {/* UPDATE: toggle che APRE l'input per digitare/rigenerare la nuova password */}
        {isEditing && (
          <>
            <Form.Item
              label="Reimposta password ora"
              name="resetPasswordOnUpdate"
              valuePropName="checked"
              tooltip="Se attivo, inserisci o genera qui sotto la nuova password."
              initialValue={false}
            >
              <Switch />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) =>
                prev.resetPasswordOnUpdate !== cur.resetPasswordOnUpdate
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('resetPasswordOnUpdate') ? (
                  <Form.Item
                    label="Nuova password"
                    name="newPassword"
                    rules={[
                      { required: true, message: 'Inserisci la nuova password' },
                      { min: 8, message: 'Minimo 8 caratteri' },
                    ]}
                  >
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Digita la nuova password…"
                        value={tempPassword ?? undefined}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempPassword(val);
                          form.setFieldsValue({ newPassword: val });
                        }}
                      />
                      <Tooltip title={showPassword ? 'Nascondi' : 'Mostra'}>
                        <Button
                          icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                          onClick={() => setShowPassword((s) => !s)}
                        />
                      </Tooltip>
                      <Tooltip title="Copia">
                        <Button icon={<CopyOutlined />} onClick={copyPwd} />
                      </Tooltip>
                      <Tooltip title="Rigenera">
                        <Button icon={<ReloadOutlined />} onClick={regenPwd} />
                      </Tooltip>
                    </Space.Compact>
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </>
        )}

        {/* CREAZIONE o UPDATE senza reset: mostra/edita la password generata */}
        {tempPassword && (!isEditing || form.getFieldValue('resetPasswordOnUpdate') === false) && (
          <Form.Item label="Password (in chiaro)">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                // in creazione è editabile, in update (senza reset) è readOnly
                readOnly={isEditing}
                type={showPassword ? 'text' : 'password'}
                value={tempPassword}
                onChange={(e) => {
                  if (!isEditing) setTempPassword(e.target.value);
                }}
                placeholder="Password iniziale"
              />
              <Tooltip title={showPassword ? 'Nascondi' : 'Mostra'}>
                <Button
                  icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() => setShowPassword((s) => !s)}
                />
              </Tooltip>
              <Tooltip title="Copia">
                <Button icon={<CopyOutlined />} onClick={copyPwd} />
              </Tooltip>
              <Tooltip title="Rigenera">
                <Button icon={<ReloadOutlined />} onClick={regenPwd} />
              </Tooltip>
            </Space.Compact>
            <Text type="secondary">
              Salvala subito: non sarà più recuperabile dopo la chiusura.
            </Text>
          </Form.Item>
        )}

        <Form.Item
          label="Attivo"
          name="active"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ManageCustomerAccessDrawer;
